import { connectSockets, disconnectSockets } from "@/hooks/useSockets";
import { getErrorMessage } from "@/utils/error";
import { config } from "@common/utils/config";
import { toast } from "@givenwell/components";
import { createAuth } from "@givenwell/fusionauth";
import {
  AddressService,
  AirWallexComponentsService,
  ApiError,
  AuthService,
  CategoriesService,
  ClaimsService,
  DataService,
  DirectInvitationService,
  HomeService,
  InvitationService,
  ListingSearchService,
  ListingsService,
  LocationService,
  MeService,
  MessagesService,
  MyNotificationsService,
  OpenAPI,
  PurchaseReviewsService,
  PurchasesService,
  RequestService,
  ResourcesService,
  SubscriptionsService,
  SuppliersService,
  TopupService,
  TransactionsService,
} from "@givenwell/marketplace-api";
import { QueryCache, QueryClient } from "@tanstack/react-query";

export const auth = createAuth({
  serverUrl: config.apiBaseUrl,
  redirectUri: config.marketplaceUrl,
  logoutUri: config.marketplaceUrl + "/sign-in",
  environmentName: config.environmentName,
  applicationName: "marketplace",
});

let refetchTokenStatus: "running" | "idle" = "idle";

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError(error) {
      if (error instanceof ApiError && error.status === 401) {
        if (refetchTokenStatus === "running") {
          return;
        }
        refetchTokenStatus = "running";
        auth
          .renewAuthTokens(true)
          .then(success => {
            if (success) {
              setTimeout(() => {
                api.auth
                  .getMarketplaceUserInfo()
                  .then(() => {
                    queryClient.refetchQueries();
                  })
                  .catch(() => {
                    auth.reset();
                    auth.redirectToLogin();
                  });
              }, 100);
            } else {
              auth.reset();
              auth.redirectToLogin();
            }
          })
          .finally(() => {
            // 30 sec grace period from attempting to refresh token before allowing another attempt.
            setTimeout(() => {
              refetchTokenStatus = "idle";
            }, 30 * 1000);
          });
      }
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
      retry: false,
      staleTime: Infinity,
    },
    mutations: {
      onSuccess() {
        queryClient.invalidateQueries();
      },
      onError(error) {
        const errorMessage = getErrorMessage(error);
        toast.error("Something went wrong", {
          description: errorMessage,
        });
      },
    },
  },
});

OpenAPI.BASE = config.apiBaseUrl;
OpenAPI.TOKEN = auth.getAccessToken;

export const api = {
  address: AddressService,
  airWallexComponents: AirWallexComponentsService,
  auth: AuthService,
  categories: CategoriesService,
  claims: ClaimsService,
  data: DataService,
  directInvitation: DirectInvitationService,
  home: HomeService,
  invitation: InvitationService,
  listings: ListingsService,
  listingSearch: ListingSearchService,
  location: LocationService,
  me: MeService,
  myNotifications: MyNotificationsService,
  messages: MessagesService,
  topup: TopupService,
  purchases: PurchasesService,
  purchaseReviews: PurchaseReviewsService,
  resources: ResourcesService,
  subscriptions: SubscriptionsService,
  suppliers: SuppliersService,
  transactions: TransactionsService,
  request: RequestService,
};

export type API = typeof api;

/**
 * Connects to the sockets server.
 * Should be called when the user is authenticated.
 */
export { connectSockets };

/**
 * Disconnects from the sockets server and logs the user out.
 */
export async function logout() {
  await disconnectSockets();
  auth.logout();
}
